import { productRepo } from "@/repositories/productRepo";
import { stripeRepo } from "@/repositories/stripeRepo";
import { capitalize, cn } from "@repo/shared/utils";
import type { PromotionCode } from "@repo/types";
import { Button } from "@repo/ui/components/Base/Button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@repo/ui/components/Base/Card";
import { Heading } from "@repo/ui/components/Base/Heading";
import { Input } from "@repo/ui/components/Base/Input";
import { Formik } from "formik";
import { CheckIcon, XIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { TermsAndConditionsDialog } from "../termsAndConditions/TermsAndConditionsDialog";
import { useGetTenant } from "@/hooks/queryHooks/tenantHooks";
import { firstValueFrom, take } from "rxjs";

type SubscriptionDisplayProps = {
  title: string;
  description: string;
  price: number;
  productId: string;
  selected?: boolean;
  selectButtonText?: string;
  interval: string;
};

const SubscriptionDisplay = ({
  title,
  description,
  price,
  productId,
  selected,
  selectButtonText,
  interval,
}: SubscriptionDisplayProps) => {
  const [, setSearchParams] = useSearchParams();
  const handleSelect = () => {
    setSearchParams((searchParams) => {
      searchParams.set("subscriptionType", productId);
      // searchParams.set(
      //   "stepId",
      //   `${parseInt(searchParams.get("stepId") ?? "0") + 1}`
      // );
      return searchParams;
    });
  };
  return (
    <Card className={cn("grid", selected && "border-2 shadow-xl")}>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        <p>
          <span className="text-3xl font-bold">
            {new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
            }).format(price / 100)}
          </span>
          /{interval === "month" ? "mo" : "yr"}
        </p>
      </CardContent>
      <CardFooter>
        {!selected && (
          <Button
            disabled={selected}
            onClick={handleSelect}
            variant={selected ? "default" : "secondary"}
          >
            {selectButtonText}
          </Button>
        )}
      </CardFooter>
    </Card>
  );
};

interface CartProps {
  subscription?: Omit<SubscriptionDisplayProps, "selected">;
  appliedDiscount?: PromotionCode | null;
}
const Cart = ({ subscription, appliedDiscount }: CartProps) => {
  const [discountCodeStatus, setDiscountCodeStatus] = useState("unknown");

  const [, setSearchParams] = useSearchParams();

  const discount = useMemo(() => {
    if (!appliedDiscount) return null;
    if (appliedDiscount.coupon.amount_off)
      return appliedDiscount.coupon.amount_off;
    if (appliedDiscount.coupon.percent_off)
      return (
        (subscription?.price ?? 0) * (appliedDiscount.coupon.percent_off / 100)
      );
    return null;
  }, [appliedDiscount, subscription]);

  const duration = useMemo(() => {
    if (!appliedDiscount) return null;
    if (appliedDiscount.coupon.duration === "once")
      return { text: "1 month", months: 1 };
    if (appliedDiscount.coupon.duration === "repeating")
      return {
        text: `${appliedDiscount.coupon.duration_in_months} months`,
        months: appliedDiscount.coupon.duration_in_months,
      };
    if (appliedDiscount.coupon.duration === "forever")
      return { text: "forever", months: 12 };
    return null;
  }, [appliedDiscount]);

  const discountTotal = useMemo(() => {
    if (!discount) return subscription?.price ?? 0;
    if (typeof discount === "number" && subscription)
      return subscription.price - discount;
    return 0;
  }, [discount, subscription]);

  const firstYearTotal = useMemo(() => {
    if (!subscription) return 0;
    if (!duration || !duration.months) return subscription.price * 12;
    return (
      discountTotal * duration.months +
      subscription.price * (12 - duration.months)
    );
  }, [discountTotal, subscription, duration]);

  return (
    <div className="w-full flex flex-col gap-4">
      <Heading title="Your Cart" />
      {subscription && (
        <div className="bg-white w-full flex flex-col gap-4">
          <div className="flex flex-row justify-between w-full text-2xl p-4">
            <span>{subscription.title}</span>
            <span>
              {new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "USD",
              }).format(subscription.price / 100)}
            </span>
          </div>
          {appliedDiscount && (
            <div className="flex flex-row justify-between w-full text-2xl p-4">
              <div className="flex flex-col">
                <span>Discount Code</span>
                <span className="text-base font-mono">
                  {appliedDiscount.code}
                </span>
              </div>
              <span className="text-lg">
                {typeof discount === "number" && (
                  <span>
                    {new Intl.NumberFormat("en-US", {
                      style: "currency",
                      currency: "USD",
                    }).format(discount / 100)}
                  </span>
                )}
                {` off for ${duration?.text}`}
              </span>
              {typeof discount === "number" && (
                <span>
                  -
                  {new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                  }).format(discount / 100)}
                </span>
              )}
            </div>
          )}

          <div className="bg-white flex flex-row justify-end w-full px-4">
            <span>--------------</span>
          </div>
          {appliedDiscount && (
            <div className="bg-white flex flex-row justify-between w-full text-2xl p-4 font-bold">
              <span>{`Total after discount`}</span>
              <span>
                {new Intl.NumberFormat("en-US", {
                  style: "currency",
                  currency: "USD",
                }).format(discountTotal / 100)}
              </span>
            </div>
          )}

          <div className="bg-white flex flex-row justify-between w-full text-2xl p-4 ">
            {duration ? (
              <span>Total After Promotional Ends</span>
            ) : (
              <span>{`${capitalize(subscription.interval)}ly`} Total</span>
            )}
            <span>
              {new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "USD",
              }).format((subscription?.price ?? 0) / 100)}
            </span>
          </div>
          {subscription.interval === "month" && (
            <>
              <div className="bg-white flex flex-row justify-between w-full text-2xl p-4 ">
                <span>First Year</span>
                <span>
                  {new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                  }).format(firstYearTotal / 100)}
                </span>
              </div>
              <div className="bg-white flex flex-row justify-between w-full text-2xl p-4 ">
                <span>Annually</span>
                <span>
                  {new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                  }).format(((subscription?.price ?? 0) * 12) / 100)}
                </span>
              </div>
            </>
          )}
        </div>
      )}

      <Formik
        initialValues={{ discountCode: "" }}
        onSubmit={async (values) => {
          const discountCode = values.discountCode;
          if (!discountCode) return;

          const coupon = await firstValueFrom(
            stripeRepo.getCoupon(discountCode)
          );

          if (coupon?.coupon?.valid) {
            setDiscountCodeStatus("accepted");
            setSearchParams((searchParams) => {
              searchParams.set("discountCode", discountCode);
              return searchParams;
            });
          } else {
            setDiscountCodeStatus("rejected");
          }
        }}
      >
        {({
          values,
          handleSubmit,
          handleBlur,
          handleChange,
          errors,
          touched,
          isSubmitting,
        }) => (
          <form
            onSubmit={handleSubmit}
            className="bg-white flex flex-row justify-start w-full text-2xl p-4 rounded-lg items-center"
          >
            <Input
              value={values.discountCode}
              onChange={handleChange}
              onBlur={handleBlur}
              error={!!errors.discountCode && touched.discountCode}
              errorMessage={errors.discountCode}
              id="discountCode"
              label="Discount Code"
              orientation="horizontal"
              containerStyle="mr-5"
            />
            <XIcon
              className={cn(
                "text-red-500 duration-500 transition-all",
                !!errors.discountCode && touched.discountCode
                  ? "visible"
                  : "hidden"
              )}
            />
            <CheckIcon
              className={cn(
                "text-green-500",
                !(!!errors.discountCode && touched.discountCode) &&
                  discountCodeStatus === "accepted"
                  ? "visible"
                  : "hidden"
              )}
            />
            <Button
              className="ml-5"
              variant="default"
              type="submit"
              disabled={isSubmitting}
            >
              Apply
            </Button>
          </form>
        )}
      </Formik>
    </div>
  );
};

interface SelectSubscriptionStepProps {
  organizationMunicipalityId?: string;
  organizationType?: string | null;
  tenantId: string;
}

export const SelectSubscriptionStep = ({
  organizationMunicipalityId,
  organizationType,
  tenantId,
}: SelectSubscriptionStepProps) => {
  const { data: tenantData } = useGetTenant(tenantId);
  const [subscriptionOptions, setSubscriptionOptions] = useState<
    Omit<SubscriptionDisplayProps, "selected">[]
  >([]);

  const [searchParams, setSearchParams] = useSearchParams();
  const productId = useMemo(
    () => searchParams.get("subscriptionType"),
    [searchParams]
  );
  const discountCode = useMemo(
    () => searchParams.get("discountCode"),
    [searchParams]
  );

  const [appliedDiscount, setAppliedDiscount] = useState<PromotionCode | null>(
    null
  );

  useEffect(() => {
    if (!discountCode) return;

    let cancel = false;

    stripeRepo
      .getCoupon(discountCode)
      .pipe(take(1))
      .subscribe((coupon) => {
        if (cancel) return;

        if (coupon?.coupon?.valid) {
          setAppliedDiscount(coupon);
        }
      });

    return () => {
      cancel = true;
    };
  }, [discountCode]);

  const selectedSubscription = useMemo(() => {
    return subscriptionOptions.find((sub) => sub.productId === productId);
  }, [productId, subscriptionOptions]);

  const handleCheckout = () => {
    setSearchParams((searchParams) => {
      searchParams.set(
        "stepId",
        `${parseInt(searchParams.get("stepId") ?? "0") + 1}`
      );
      return searchParams;
    });
  };

  useEffect(() => {
    const lookupSearch = organizationMunicipalityId; // In the form ##-X i.e. 1108-Erie
    if (!lookupSearch || !organizationType) return;
    productRepo
      .getForMunicipality(lookupSearch)
      .then((res) => setSubscriptionOptions(res.prices));
  }, [organizationMunicipalityId, organizationType]);

  const [termsDialogOpen, setTermsDialogOpen] = useState(false);

  return (
    <div className="flex flex-col gap-4">
      <TermsAndConditionsDialog
        tenant={tenantData ?? undefined}
        open={termsDialogOpen}
        setOpen={setTermsDialogOpen}
        allowCancel
      />
      <div className="grid grid-col-1 md:grid-cols-1 lg:grid-cols-3 gap-x-2 gap-y-4">
        {subscriptionOptions.map((subscription, index) => (
          <SubscriptionDisplay
            key={`${subscription.productId}-${index}`}
            {...subscription}
            selected={productId === subscription.productId}
            selectButtonText={
              subscriptionOptions.length === 1 ? "Confirm" : "Add to Cart"
            }
          />
        ))}
      </div>
      <div className="w-full">
        <Cart
          subscription={selectedSubscription}
          appliedDiscount={appliedDiscount}
        />
      </div>

      <div className="w-full flex flex-row justify-end">
        <Button
          disabled={tenantData?.ownerTermsAndConditionsAccepted}
          onClick={() => setTermsDialogOpen(true)}
        >
          Accept Terms and Conditions
        </Button>
      </div>
      <div className="w-full flex flex-row justify-end">
        <Button
          disabled={
            !selectedSubscription ||
            !tenantData?.ownerTermsAndConditionsAccepted
          }
          onClick={handleCheckout}
        >
          Checkout
        </Button>
      </div>
      <div className="pt-16 w-full">
        <p>
          CiviLink partners with Stripe to provide easy checkout and
          subscription management. To pay by check, continue checking out, and
          contact CiviLink at{" "}
          <a href="mailto:contact@civi.link" className="underline">
            contact@civi.link
          </a>{" "}
          later for invoicing and payment options.
        </p>
      </div>
    </div>
  );
};
